import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import type {
  Session,
  AdminSessionActionRequest,
  AdminSessionMessageRequest,
  BulkSessionActionRequest
} from '@/types/api';
import { SessionState } from '@/types/api';

const ADMIN_QUERY_KEYS = {
  adminSessions: ['admin', 'sessions'] as const,
};

export function useAdminSessions() {
  return useQuery({
    queryKey: ADMIN_QUERY_KEYS.adminSessions,
    queryFn: () => apiClient.getAllSessions(),
    refetchInterval: 60000, // Refresh every 60 seconds (reduced from 30s to prevent excessive refetches)
    staleTime: 30000, // Consider data stale after 30 seconds (increased from 10s for better performance)
  });
}

export function useAdminDisconnectSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, request }: { sessionId: string; request: AdminSessionActionRequest }) =>
      apiClient.adminDisconnectSession(sessionId, request),
    onSuccess: () => {
      // Invalidate and refetch admin sessions
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });
    },
    onMutate: async ({ sessionId }) => {
      // Optimistic update - mark session as disconnecting
      await queryClient.cancelQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });

      const previousSessions = queryClient.getQueryData<Session[]>(ADMIN_QUERY_KEYS.adminSessions);

      if (previousSessions) {
        const updatedSessions = previousSessions.map(session =>
          session.id === sessionId
            ? { ...session, sessionState: SessionState.Disconnected }
            : session
        );
        queryClient.setQueryData<Session[]>(ADMIN_QUERY_KEYS.adminSessions, updatedSessions);
      }

      return { previousSessions };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSessions) {
        queryClient.setQueryData(ADMIN_QUERY_KEYS.adminSessions, context.previousSessions);
      }
    },
  });
}

export function useAdminLogOffSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, request }: { sessionId: string; request: AdminSessionActionRequest }) =>
      apiClient.adminLogOffSession(sessionId, request),
    onSuccess: () => {
      // Invalidate and refetch admin sessions
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });
    },
  });
}

export function useAdminSendMessage() {
  return useMutation({
    mutationFn: ({ sessionId, request }: { sessionId: string; request: AdminSessionMessageRequest }) =>
      apiClient.adminSendMessageToSession(sessionId, request),
    onSuccess: () => {
      // Note: No need to invalidate sessions for message sending as session state doesn't change
      // Could add a toast notification here instead
    },
  });
}

export function useBulkSessionAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BulkSessionActionRequest) =>
      apiClient.bulkSessionAction(request),
    onSuccess: () => {
      // Invalidate and refetch admin sessions after bulk action
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });
    },
  });
}

export function useForceHealthCheck() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.forceHealthCheck(),
    onSuccess: () => {
      // Invalidate and refetch admin sessions after health check
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });
    },
  });
}

export function useForceRefresh() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.forceRefresh(),
    onSuccess: () => {
      // Invalidate and refetch admin sessions after force refresh
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.adminSessions });
    },
  });
}